import React, { useState, useEffect, useCallback, useRef } from "react";
import "./CustomAudit.css";

/**
 * Custom Audit Builder
 * ---------------------------------------------------------------------
 * Lets the user bolt a manually-authored section (title, findings text,
 * images, videos, a takeaway note) onto the automated report, at a
 * position of their choosing. Not scored — purely presentational.
 *
 * Props:
 *   apiBaseUrl   base for the backend, e.g. "" or "https://api.example.com"
 *   brandName    string — required. The only piece of context this needs;
 *                every card is scoped to this brand, same as the rest of
 *                the wizard's localStorage caches (domainAuthority_<brand>,
 *                technicalSEO_<brand>, etc). Everything else on the card
 *                (title, notes, images, videos) is filled in by hand.
 *   onContinue   () => void — called after "Continue to Final Verdict"
 *
 * Exactly one of the CustomAudit's "images" or "videos" arrays holding
 * many entries at once is the whole point here — everything below is
 * built around multi-file selection.
 */

const POSITION_OPTIONS = [
  { value: "card-4", label: "After Overall Brand Scores" },
  { value: "card-9", label: "After AI Visibility" },
  { value: "card-5", label: "After SEO / Domain & Backlink Comparison" },
  { value: "card-6", label: "After Page Speed Audit" },
  { value: "card-7", label: "After Technical SEO" },
  { value: "card-8", label: "After GMB Map Ranking" },
  { value: "card-custom-builder", label: "Right here — just before the Final Verdict" },
];

const ACCEPTED_VIDEO_EXT = ".mp4,.mov,.webm,.avi,.mkv,.m4v,.ogv";

const emptyForm = () => ({
  title: "",
  position: "card-custom-builder",
  text: "",
  note: "",
  images: [], // { id, previewUrl, file, uploading, error, uploaded: {url, filename, size} }
  videoRows: [], // { id, mode: 'url'|'upload', url, file, previewUrl, uploading, error, uploaded }
});

function uid() {
  return Math.random().toString(36).slice(2, 10);
}

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:10000';

export default function CustomAudit({ apiBaseUrl = API_BASE_URL, brandName, onContinue }) 
{  const [form, setForm] = useState(emptyForm());
  const [addedCards, setAddedCards] = useState([]);
  const [savingCard, setSavingCard] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const [formError, setFormError] = useState("");
  const [loadingExisting, setLoadingExisting] = useState(true);

  const imgInputRef = useRef(null);

  const listUrl = brandName
    ? `${apiBaseUrl}/api/v1/custom-audit/brand/${encodeURIComponent(brandName)}`
    : null;

  // ── Load any cards already added (e.g. user navigated away and back) ──
  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!listUrl) {
        setLoadingExisting(false);
        return;
      }
      try {
        const res = await fetch(listUrl);
        const json = await res.json();
        if (!cancelled && json.success) setAddedCards(json.data);
      } catch (e) {
        console.error("Failed to load existing custom audit cards", e);
      } finally {
        if (!cancelled) setLoadingExisting(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [listUrl]);

  const updateField = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  // ── Image upload ─────────────────────────────────────────────────────
  const handleImageUpload = useCallback(
    async (event) => {
      const files = Array.from(event.target.files || []);
      event.target.value = ""; // allow re-selecting the same file later
      if (!files.length) return;

      const entries = files.map((file) => ({
        id: uid(),
        file,
        previewUrl: URL.createObjectURL(file),
        uploading: true,
        error: "",
        uploaded: null,
      }));
      setForm((f) => ({ ...f, images: [...f.images, ...entries] }));

      entries.forEach(async (entry) => {
        try {
          const body = new FormData();
          body.append("file", entry.file);
          const res = await fetch(`${apiBaseUrl}/api/v1/custom-audit/upload/image`, {
            method: "POST",
            body,
          });
          const json = await res.json();
          if (!res.ok || !json.success) throw new Error(json.detail || "Upload failed");
          setForm((f) => ({
            ...f,
            images: f.images.map((im) =>
              im.id === entry.id ? { ...im, uploading: false, uploaded: json } : im
            ),
          }));
        } catch (e) {
          setForm((f) => ({
            ...f,
            images: f.images.map((im) =>
              im.id === entry.id ? { ...im, uploading: false, error: e.message } : im
            ),
          }));
        }
      });
    },
    [apiBaseUrl]
  );

  const removeImage = async (id) => {
    const target = form.images.find((im) => im.id === id);
    setForm((f) => ({ ...f, images: f.images.filter((im) => im.id !== id) }));
    if (target?.uploaded?.url) {
      fetch(`${apiBaseUrl}/api/v1/custom-audit/upload?path=${encodeURIComponent(target.uploaded.url)}`, {
        method: "DELETE",
      }).catch(() => {});
    }
    if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
  };

  // ── Video rows (either a pasted link, or an uploaded file) ────────────
  const addVideoUrlRow = () => {
    setForm((f) => ({
      ...f,
      videoRows: [
        ...f.videoRows,
        { id: uid(), mode: "url", url: "", file: null, previewUrl: "", uploading: false, error: "", uploaded: null },
      ],
    }));
  };

  const removeVideoRow = (id) => {
    const target = form.videoRows.find((v) => v.id === id);
    setForm((f) => ({ ...f, videoRows: f.videoRows.filter((v) => v.id !== id) }));
    if (target?.uploaded?.url) {
      fetch(`${apiBaseUrl}/api/v1/custom-audit/upload?path=${encodeURIComponent(target.uploaded.url)}`, {
        method: "DELETE",
      }).catch(() => {});
    }
    if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
  };

  const setVideoRowUrl = (id, url) => {
    setForm((f) => ({
      ...f,
      videoRows: f.videoRows.map((v) => (v.id === id ? { ...v, url } : v)),
    }));
  };

  const handleVideoFileSelect = async (id, fileList) => {
    const file = fileList?.[0];
    if (!file) return;
    const previewUrl = URL.createObjectURL(file);
    setForm((f) => ({
      ...f,
      videoRows: f.videoRows.map((v) =>
        v.id === id ? { ...v, mode: "upload", file, previewUrl, uploading: true, error: "" } : v
      ),
    }));

    try {
      const body = new FormData();
      body.append("file", file);
      const res = await fetch(`${apiBaseUrl}/api/v1/custom-audit/upload/video`, {
        method: "POST",
        body,
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.detail || "Upload failed");
      setForm((f) => ({
        ...f,
        videoRows: f.videoRows.map((v) => (v.id === id ? { ...v, uploading: false, uploaded: json } : v)),
      }));
    } catch (e) {
      setForm((f) => ({
        ...f,
        videoRows: f.videoRows.map((v) => (v.id === id ? { ...v, uploading: false, error: e.message } : v)),
      }));
    }
  };

  // ── Build the payload for one card ────────────────────────────────────
  const buildPayload = () => {
    const images = form.images
      .filter((im) => im.uploaded)
      .map((im) => ({ id: im.uploaded.id, url: im.uploaded.url, filename: im.uploaded.filename, size: im.uploaded.size }));

    const videos = form.videoRows
      .map((v) => {
        if (v.mode === "upload" && v.uploaded) {
          return { id: v.uploaded.id, type: "upload", url: v.uploaded.url, filename: v.uploaded.filename, size: v.uploaded.size };
        }
        if (v.mode === "url" && v.url.trim()) {
          return { id: v.id, type: "url", url: v.url.trim(), filename: null, size: null };
        }
        return null;
      })
      .filter(Boolean);

    return {
      brand_name: brandName,
      title: form.title.trim(),
      position: form.position,
      findings_text: form.text,
      inference_note: form.note,
      images,
      videos,
    };
  };

  const stillUploading = () =>
    form.images.some((im) => im.uploading) || form.videoRows.some((v) => v.uploading);

  const addCustomCard = async () => {
    setFormError("");
    if (!form.title.trim()) {
      setFormError("Give this card a title before adding it.");
      return;
    }
    if (!brandName) {
      setFormError("Missing brand name — can't save this card yet.");
      return;
    }
    if (stillUploading()) {
      setFormError("Hang on — some media is still uploading.");
      return;
    }

    setSavingCard(true);
    try {
      const res = await fetch(`${apiBaseUrl}/api/v1/custom-audit/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(buildPayload()),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.detail || "Could not save this card");
      setAddedCards((prev) => [...prev, json.data]);
      setForm(emptyForm());
    } catch (e) {
      setFormError(e.message);
    } finally {
      setSavingCard(false);
    }
  };

  const deleteAddedCard = async (id) => {
    setAddedCards((prev) => prev.filter((c) => c.id !== id));
    try {
      await fetch(`${apiBaseUrl}/api/v1/custom-audit/${id}`, { method: "DELETE" });
    } catch (e) {
      console.error("Failed to delete custom audit card", e);
    }
  };

  const completeCustomBuilder = async () => {
    setFormError("");
    // If the user filled the form but never clicked "+ Add This Card",
    // save it for them on the way out rather than silently dropping it.
    const hasUnsavedContent =
      form.title.trim() || form.text.trim() || form.note.trim() || form.images.length || form.videoRows.length;

    setFinishing(true);
    try {
      if (hasUnsavedContent) {
        if (stillUploading()) {
          setFormError("Hang on — some media is still uploading.");
          setFinishing(false);
          return;
        }
        if (!form.title.trim()) {
          setFormError("Give this last card a title, or clear the form, before continuing.");
          setFinishing(false);
          return;
        }
        await addCustomCard();
      }
      onContinue?.();
    } finally {
      setFinishing(false);
    }
  };

  const positionLabel = (value) => POSITION_OPTIONS.find((p) => p.value === value)?.label || value;

  return (
    <div className="card" id="card-custom-builder">
      <div className="eyebrow">Optional</div>
      <h2 className="ctitle">Add a Custom Audit</h2>
      <p className="cdesc">
        Add your own manual findings — brand voice, ad creative, content quality, anything the automated
        audit doesn't cover. Attach text, images and videos. These sections aren't scored and won't affect
        the overall score.
      </p>

      <div className="cab-field">
        <div className="flbl">Title</div>
        <input
          className="tinput"
          type="text"
          placeholder="e.g. Content & Brand Voice Review"
          value={form.title}
          onChange={(e) => updateField("title", e.target.value)}
        />
      </div>

      <div className="cab-field">
        <div className="flbl">Where should this go?</div>
        <select className="cab-sel" value={form.position} onChange={(e) => updateField("position", e.target.value)}>
          {POSITION_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      <div className="cab-field">
        <div className="flbl">Findings / notes</div>
        <textarea
          className="tarea"
          placeholder="Write your findings here…"
          value={form.text}
          onChange={(e) => updateField("text", e.target.value)}
        />
      </div>

      {/* ── Images ── */}
      <div className="cab-field">
        <div className="flbl">Images</div>
        <div className="imgup-zone" onClick={() => imgInputRef.current?.click()}>
          <svg viewBox="0 0 24 24">
            <path d="M19 5v14H5V5h14m0-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-4.86 8.86l-3 3.87L9 13.14 6 17h12l-3.86-5.14z" />
          </svg>
          <div className="imgup-hint">Click to upload screenshots or images</div>
          <input
            ref={imgInputRef}
            type="file"
            accept="image/*"
            multiple
            style={{ display: "none" }}
            onChange={handleImageUpload}
          />
        </div>

        {form.images.length > 0 && (
          <div className="imgup-grid">
            {form.images.map((im) => (
              <div className="imgup-thumb" key={im.id}>
                <img src={im.uploaded?.url ? `${apiBaseUrl}${im.uploaded.url}` : im.previewUrl} alt="" />
                {im.uploading && <div className="imgup-overlay">Uploading…</div>}
                {im.error && <div className="imgup-overlay imgup-overlay-error">{im.error}</div>}
                <button
                  type="button"
                  className="imgup-remove"
                  onClick={() => removeImage(im.id)}
                  aria-label="Remove image"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Videos ── */}
      <div className="cab-field">
        <div className="flbl">Videos</div>
        <div className="cab-video-hint">Paste a link, or upload a video file (mp4, mov, webm, avi, mkv, m4v).</div>
        <div id="cab-video-rows">
          {form.videoRows.map((row) => (
            <div className="vidrow" key={row.id}>
              <div className="vidrow-toggle">
                <button
                  type="button"
                  className={row.mode === "url" ? "vidrow-tab active" : "vidrow-tab"}
                  onClick={() => setForm((f) => ({ ...f, videoRows: f.videoRows.map((v) => (v.id === row.id ? { ...v, mode: "url" } : v)) }))}
                >
                  Link
                </button>
                <button
                  type="button"
                  className={row.mode === "upload" ? "vidrow-tab active" : "vidrow-tab"}
                  onClick={() => setForm((f) => ({ ...f, videoRows: f.videoRows.map((v) => (v.id === row.id ? { ...v, mode: "upload" } : v)) }))}
                >
                  Upload
                </button>
              </div>

              {row.mode === "url" ? (
                <input
                  className="tinput vidrow-input"
                  type="url"
                  placeholder="https://…"
                  value={row.url}
                  onChange={(e) => setVideoRowUrl(row.id, e.target.value)}
                />
              ) : (
                <div className="vidrow-file">
                  <label className="vidrow-file-btn">
                    {row.uploaded ? "Replace file" : "Choose file"}
                    <input
                      type="file"
                      accept={`video/*,${ACCEPTED_VIDEO_EXT}`}
                      style={{ display: "none" }}
                      onChange={(e) => handleVideoFileSelect(row.id, e.target.files)}
                    />
                  </label>
                  {row.uploading && <span className="vidrow-status">Uploading…</span>}
                  {row.error && <span className="vidrow-status vidrow-status-error">{row.error}</span>}
                  {row.uploaded && !row.uploading && !row.error && (
                    <span className="vidrow-status">{row.uploaded.filename}</span>
                  )}
                  {row.previewUrl && (
                    <video className="vidrow-preview" src={row.previewUrl} controls muted />
                  )}
                </div>
              )}

              <button type="button" className="vidrow-remove" onClick={() => removeVideoRow(row.id)} aria-label="Remove video">
                ×
              </button>
            </div>
          ))}
        </div>
        <button type="button" className="vidurl-add" onClick={addVideoUrlRow}>
          + Add another video
        </button>
      </div>

      <div className="cab-field">
        <div className="flbl">Inference / note</div>
        <textarea
          className="tarea"
          style={{ minHeight: 64 }}
          placeholder="What should the client take away from this section?"
          value={form.note}
          onChange={(e) => updateField("note", e.target.value)}
        />
      </div>

      {formError && <div className="cab-error">{formError}</div>}

      <div className="cab-btnrow">
        <button className="btn btn-ghost" type="button" style={{ marginTop: 0 }} onClick={addCustomCard} disabled={savingCard}>
          {savingCard ? "Adding…" : "+ Add This Card"}
        </button>
        <button className="btn" style={{ marginTop: 0 }} onClick={completeCustomBuilder} disabled={finishing}>
          {finishing ? "Saving…" : "Continue to Final Verdict"}
          <svg viewBox="0 0 24 24" fill="white">
            <path d="M12 4l-1.4 1.4L16.2 11H4v2h12.2l-5.6 5.6L12 20l8-8z" />
          </svg>
        </button>
      </div>

      {!loadingExisting && addedCards.length > 0 && (
        <div className="cab-added-list">
          {addedCards.map((card) => (
            <div className="cab-added-item" key={card.id}>
              <div className="cab-added-main">
                <div className="cab-added-title">{card.title}</div>
                <div className="cab-added-meta">
                  {positionLabel(card.position)} · {(card.images || []).length} image
                  {(card.images || []).length === 1 ? "" : "s"} · {(card.videos || []).length} video
                  {(card.videos || []).length === 1 ? "" : "s"}
                </div>
              </div>
              <button type="button" className="cab-added-remove" onClick={() => deleteAddedCard(card.id)}>
                Remove
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}